import type { WorkloadFlag, WorkloadTotals } from '@exigo/shared';
import type { ReferenceData } from '../../api/types';
import styles from './WorkloadMix.module.css';

interface WorkloadMixProps {
  totals: WorkloadTotals | null;
  flags: readonly WorkloadFlag[];
  categories: ReferenceData['workloadCategories'];
}

/**
 * The consumption mix, and the engine's advisory flags on it.
 *
 * Both come straight from `/evaluate` — this component does no sizing arithmetic
 * of its own beyond turning category totals into bar percentages. The flags are
 * the most actionable output the tool produces (one of them names the exact extra
 * spend needed to clear the Defender sub-floor), and the UI previously computed
 * and then discarded them.
 *
 * Baseline categories are excluded from the bar: the bar shows the incremental
 * mix, which is what the bands actually measure.
 */
export function WorkloadMix({ totals, flags, categories }: WorkloadMixProps): React.JSX.Element | null {
  if (!totals) return null;

  const byCategory = Object.entries(totals.byCategory)
    .map(([key, cents]) => {
      const category = categories.find((c) => c.key === key);
      return {
        key,
        cents,
        label: category?.label ?? key,
        colour: category?.colour ?? '#9096a8',
        isBaseline: category?.isBaseline === true,
      };
    })
    .filter((s) => s.cents > 0 && !s.isBaseline)
    .sort((a, b) => b.cents - a.cents);

  const total = byCategory.reduce((sum, s) => sum + s.cents, 0);
  const hasMix = total > 0;

  if (!hasMix && flags.length === 0) return null;

  return (
    <div className={styles.wrap}>
      {hasMix && (
        <>
          <div
            className={styles.bar}
            role="img"
            aria-label={`Consumption mix: ${byCategory
              .map((s) => `${s.label} ${String(Math.round((s.cents / total) * 100))}%`)
              .join(', ')}`}
          >
            {byCategory.map((s) => (
              <div
                key={s.key}
                className={styles.seg}
                style={{ width: `${String((s.cents / total) * 100)}%`, background: s.colour }}
                title={`${s.label} — ${String(Math.round((s.cents / total) * 100))}%`}
              />
            ))}
          </div>

          <ul className={styles.legend}>
            {byCategory.map((s) => (
              <li key={s.key} className={styles.legendItem}>
                <span className={styles.swatch} style={{ background: s.colour }} aria-hidden="true" />
                <span className={styles.legendLabel}>{s.label}</span>
                <span className={styles.legendPct}>{Math.round((s.cents / total) * 100)}%</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {flags.length > 0 && (
        <ul className={styles.flags}>
          {flags.map((f) => (
            <li key={f.kind} className={styles.flag}>
              {f.message}
            </li>
          ))}
        </ul>
      )}

      <p className={styles.note}>
        Anything tagged <b>Existing environment</b> becomes the baseline and is excluded from the
        incremental total the bands are measured on. Database, Fabric and Defender rows feed their
        sub-floor tests automatically, so you never enter those figures twice — which also means{' '}
        <b>the security portion of a server line belongs on its own Defender row</b>, not bundled
        into the server cost.
      </p>
    </div>
  );
}
